import * as Sentry from '@sentry/react';
import { type Mutation, type Query } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { type NormalizedError } from '~/types/errors';
import { MutationMeta, QueryMeta } from '~/types/query.type';

import { VestigasHTTPError } from '@vestigas/data';

import AuthService from '~/services/auth.service';
import ToastService from '~/services/toast.service';

// Imported directly rather than through '~/utils': this module is part of that barrel.
import i18n from './i18n';

/**
 * Type guard to check if an error is a VestigasHTTPError
 */
function isVestigasHTTPError(error: unknown): error is VestigasHTTPError {
  return error instanceof VestigasHTTPError;
}

/**
 * Type guard to check if an error is an AxiosError
 */
function isAxiosError(error: unknown): error is AxiosError {
  return error instanceof AxiosError;
}

/**
 * Error Factory for centralized error handling in TanStack Query.
 *
 * This class normalizes errors from various sources (HTTPError, AxiosError, network errors, etc.)
 * and routes them to the appropriate display mechanism (toast, error boundary, inline validation).
 *
 * Based on the error handling strategy defined in:
 * https://www.notion.so/vestigas/Error-handling-265ac08e558c80d19051edcc61746690
 */
export class ErrorFactory {
  /**
   * Normalizes errors from various sources into a consistent format.
   *
   * @param error - The error to normalize (can be VestigasHTTPError, AxiosError, or any other error)
   * @returns Normalized error object
   */
  private normalizeError(error: unknown): NormalizedError {
    // Handle VestigasHTTPError from ky (all ky errors are converted to this type)
    if (isVestigasHTTPError(error)) {
      const status = error.response.status;
      let message = error.message || `HTTP Error ${status}`;

      const { parsedBody } = error;

      // Handle plain string error messages
      if (typeof parsedBody === 'string') {
        message = parsedBody;
      }
      // Extract error message from detail field
      else if (
        parsedBody &&
        typeof parsedBody === 'object' &&
        'detail' in parsedBody &&
        typeof parsedBody.detail === 'string'
      ) {
        message = parsedBody.detail;
      }

      return {
        message,
        originalError: error,
        status,
        type: this.getErrorTypeFromStatus(status),
      };
    }

    // Handle AxiosError
    if (isAxiosError(error)) {
      return {
        message: error.message || 'Request failed',
        originalError: error,
        status: error.response?.status,
        type: error.response?.status
          ? this.getErrorTypeFromStatus(error.response.status)
          : 'HTTP',
      };
    }

    // Handle standard Error objects
    if (error instanceof Error) {
      return {
        message: error.message || 'An unexpected error occurred',
        originalError: error,
        type: 'UNKNOWN',
      };
    }

    // Handle unknown error types
    return {
      message: 'An unexpected error occurred',
      originalError: error,
      type: 'UNKNOWN',
    };
  }

  /**
   * Determines error type based on HTTP status code.
   */
  private getErrorTypeFromStatus(status: number): string {
    if (status === 401 || status === 403) {
      return 'AUTH';
    }

    if (status === 422 || status === 400) {
      return 'VALIDATION';
    }

    if (status >= 500) {
      return 'SERVER';
    }

    return 'HTTP';
  }

  /**
   * Captures an error with a trace.
   * @param error - The normalized error
   * @param level - The severity level
   */
  private captureErrorWithTrace(
    error: NormalizedError,
    level: Sentry.SeverityLevel = 'error',
  ): void {
    const scope = Sentry.getCurrentScope();
    const propagationContext = scope.getPropagationContext();

    // Generate a random span ID for the error
    const span_id = Array.from({ length: 16 }, () =>
      Math.floor(Math.random() * 16).toString(16),
    ).join('');

    const extra: Record<string, unknown> = {};

    // Add VestigasHTTPError-specific context
    if (isVestigasHTTPError(error.originalError)) {
      // Add parsed error body from VestigasHTTPError
      if (error.originalError.parsedBody) {
        extra.parsedBody = error.originalError.parsedBody;
      }

      // Try to parse and add request body
      if (error.originalError.options?.body) {
        try {
          extra.requestBody = JSON.parse(
            error.originalError.options.body as string,
          );
        } catch {
          // If parsing fails, just store the raw body
          extra.requestBody = error.originalError.options.body;
        }
      }
    }

    Sentry.captureException(error.originalError, {
      level,
      extra,
      contexts: {
        trace: {
          parent_span_id: propagationContext.parentSpanId,
          span_id,
          trace_id: propagationContext.traceId,
        },
        ...(isVestigasHTTPError(error.originalError) && {
          response: {
            status_code: error.originalError.response.status,
            type: error.originalError.response.type,
          },
        }),
        ...(isAxiosError(error.originalError) &&
          error.originalError.response && {
            response: {
              status_code: error.originalError.response.status,
            },
          }),
      },
    });
  }

  /**
   * Handles auth errors with redirect (for queries).
   * Shows toast and throws error to trigger error boundary redirect.
   */
  private handleAuthErrorWithRedirect(): void {
    ToastService.error([
      ToastService.MESSAGE.UNAUTHORIZED_ERROR,
      ToastService.MESSAGE.UNAUTHORIZED_ERROR_REDIRECT,
    ]);
  }

  /**
   * Handles auth errors with toast only (for mutations).
   * Does not throw to avoid crashing the page.
   */
  private handleAuthErrorToast(error: NormalizedError): void {
    if (error.status === 401) {
      ToastService.error([ToastService.MESSAGE.UNAUTHORIZED_ERROR]);
    } else if (error.status === 403) {
      ToastService.error(['Fehlende Berechtigung']);
    }
  }

  /**
   * Handles server errors by throwing for error boundary (for queries).
   * Shows toast and throws error to display error boundary page.
   */
  private handleServerErrorWithThrow(error: NormalizedError): never {
    // Throw to trigger error boundary for 404/500+ pages
    if (error.originalError instanceof Error) {
      throw error.originalError;
    }
    // If it's not an Error object, wrap it in one
    throw new Error(error.message);
  }

  /**
   * Handles server errors with toast only (for mutations).
   * Does not throw to avoid crashing the page.
   */
  private handleServerErrorToast(): void {
    ToastService.error([
      'Fehler beim Verarbeiten der Anfrage',
      ToastService.MESSAGE.CONTACT_SUPPORT,
    ]);
  }

  /**
   * Handles rate limits by telling the user to wait rather than to retry now.
   *
   * The request is retried automatically once the server's window has passed
   * (see `queryClient`), so the toast only has to explain the pause.
   */
  private handleRateLimitError(): void {
    ToastService.error(
      [i18n.t(($) => $.error.rateLimit.message, { ns: 'common' })],
      ToastService.ID.RATE_LIMIT_ERROR,
    );
  }

  /**
   * Handles network errors by showing a toast notification.
   */
  private handleNetworkError(): void {
    ToastService.error([
      'Netzwerkfehler',
      'Bitte überprüfe deine Internetverbindung.',
    ]);
  }

  /**
   * Handles generic/unhandled errors by showing a toast notification.
   * This is the fallback handler for errors that don't match any specific category.
   *
   * @param error - The normalized error
   */
  private handleGenericError(): void {
    ToastService.error(['Ein unerwarteter Fehler ist aufgetreten']);
  }

  /**
   * Gets the error meta from the query or mutation options.
   */
  private getErrorMeta(
    query: Query<unknown, unknown> | Mutation<unknown, unknown>,
  ): QueryMeta | MutationMeta | null {
    const { meta } = query.options;

    if (!meta) return null;

    return meta;
  }

  /**
   * Checks if the error should be suppressed.
   */
  private shouldSuppressError(
    normalizedError: NormalizedError,
    errorMeta: QueryMeta | MutationMeta | null,
  ): boolean {
    const hasSuppressedErrorCode =
      !!normalizedError.status &&
      !!errorMeta?.suppressErrorForErrorCodes?.includes(normalizedError.status);
    const shouldSuppressError =
      errorMeta?.suppressError || hasSuppressedErrorCode;

    return shouldSuppressError;
  }

  /**
   * Checks if the query is the user data query.
   * Query key format: ['users', 'data', { includeCompanyInfo }]
   */
  private isUserDataQuery(query: Query<unknown, unknown>): boolean {
    const queryKey = query.queryKey;
    return (
      Array.isArray(queryKey) &&
      queryKey[0] === 'users' &&
      queryKey[1] === 'data'
    );
  }

  /**
   * Handles errors from query operations (GET requests).
   * May throw errors to trigger error boundaries for 404/500+ and auth errors.
   * If the query already has cached data (background refetch failure), shows toast instead of throwing.
   */
  public handleQueryError(
    error: unknown,
    query: Query<unknown, unknown>,
  ): void {
    const normalizedError = this.normalizeError(error);
    const errorMeta = this.getErrorMeta(query);
    const shouldSuppressError = this.shouldSuppressError(
      normalizedError,
      errorMeta,
    );

    if (shouldSuppressError) return;

    const hasCachedData = query.state.data !== undefined;

    // 401 auth errors - not logged in
    if (normalizedError.status === 401) {
      if (hasCachedData) {
        // Background refetch failed - just show toast
        this.handleAuthErrorToast(normalizedError);
      } else {
        // Initial load failed - toast
        this.handleAuthErrorWithRedirect();
      }
      return;
    }

    // 403 auth errors - no permission
    if (normalizedError.status === 403) {
      // Never show toasts for 403 errors
      return;
    }

    // User not found in database - logout
    // This happens when user is authenticated in Keycloak but doesn't exist in the app database
    if (normalizedError.status === 404 && this.isUserDataQuery(query)) {
      void AuthService.logout();
      return;
    }

    // 404 and 500+ errors
    if (
      normalizedError.status === 404 ||
      (normalizedError.status && normalizedError.status >= 500)
    ) {
      if (hasCachedData) {
        // Background refetch failed - just show toast
        this.captureErrorWithTrace(normalizedError);
        this.handleServerErrorToast();
      } else {
        // Initial load failed - throw for error boundary
        this.handleServerErrorWithThrow(normalizedError);
      }
      return;
    }

    // Rate limit - toast only. Not a bug and not worth a Sentry issue: the API logs
    // every rate-limit hit together with the route and the user who caused it.
    if (normalizedError.status === 429) {
      this.handleRateLimitError();
      return;
    }

    // Network errors - toast only
    if (normalizedError.type === 'NETWORK') {
      this.captureErrorWithTrace(normalizedError, 'warning');
      this.handleNetworkError();
      return;
    }

    // Generic fallback
    this.captureErrorWithTrace(normalizedError);
    this.handleGenericError();
  }

  /**
   * Handles errors from mutation operations (POST/PUT/DELETE requests).
   * Never throws - always shows toast notifications to avoid crashing the page.
   */
  public handleMutationError(
    error: VestigasHTTPError | AxiosError | Error,
    mutation: Mutation<unknown, unknown>,
  ): void {
    const normalizedError = this.normalizeError(error);
    const errorMeta = this.getErrorMeta(mutation);
    const shouldSuppressError = this.shouldSuppressError(
      normalizedError,
      errorMeta,
    );
    const isValidationError =
      error instanceof VestigasHTTPError &&
      (error.response.status === 422 ||
        error.response.status === 400 ||
        error.response.status === 409);

    if (isValidationError || shouldSuppressError) return;

    // 401 and 403 auth errors - toast only (no redirect for mutations)
    if (normalizedError.status === 401 || normalizedError.status === 403) {
      this.handleAuthErrorToast(normalizedError);
      return;
    }

    // 404 and 500+ errors - toast only (no error boundary)
    if (
      normalizedError.status === 404 ||
      (normalizedError.status && normalizedError.status >= 500)
    ) {
      this.captureErrorWithTrace(normalizedError);
      this.handleServerErrorToast();
      return;
    }

    // Rate limit - toast only. Not a bug and not worth a Sentry issue: the API logs
    // every rate-limit hit together with the route and the user who caused it.
    if (normalizedError.status === 429) {
      this.handleRateLimitError();
      return;
    }

    // Network errors - toast only
    if (normalizedError.type === 'NETWORK') {
      this.captureErrorWithTrace(normalizedError, 'warning');
      this.handleNetworkError();
      return;
    }

    // Generic fallback
    this.captureErrorWithTrace(normalizedError);
    this.handleGenericError();
  }
}

/**
 * Global instance of the Error Factory.
 * This should be used in the QueryClient configuration.
 */
export const errorFactory = new ErrorFactory();
